/**
 * UploadPolicyDialog — the "Neue Police" upload modal (DMS docs-fix item 1).
 *
 * Wires the previously-dead "Neue Police" buttons (PoliciesScreen + KundeDetailScreen)
 * to the existing `POST /policies/upload` (Story 9.5): a PDF is uploaded against a holder
 * (VN = firma/person), Docpier extraction classifies it, and the Police is created/versioned
 * + linked to the client — all async via the queue, so the 202 response carries no policy id
 * (the parent refreshes the list + toasts; there is no synchronous policy to navigate to).
 *
 * Presentational + stateful (the `UploadDocumentDialog` precedent): the parent injects
 * `onUpload` (a live `apiClient.policies.upload` call or a mock) and, for the un-prefilled
 * entry point (the Policies page), `onSearchHolders` (a live customer typeahead). From the
 * Kunde detail the holder is fixed via `prefilledHolder`, so no search renders.
 *
 * [Source: apps/web/src/features/documents/components/UploadDocumentDialog.tsx] (modal shell)
 * [Source: apps/api/src/modules/policy/policy-upload.controller.ts#uploadPolicy]
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Search, Upload, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import type { PolicyUploadResult } from '../../../lib/api-client';

export type PolicyDocKind = 'police_neu' | 'police_mutation';

const DOC_KIND_LABELS: Record<PolicyDocKind, string> = {
  police_neu: 'Neue Police',
  police_mutation: 'Mutation (bestehende Police)',
};

/** One selectable holder (VN) — firma or person. */
export interface HolderOption {
  id: string;
  name: string;
  /** Optional secondary line (e.g. city, or "Person"/"Firma"). */
  subtitle?: string | null;
}

export interface UploadPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fixed holder (from the Kunde detail) — when set, the holder picker is skipped/read-only. */
  prefilledHolder?: HolderOption | null;
  /** Perform the upload (parent wires the live/mock call). Resolves on success. */
  onUpload: (
    file: File,
    opts: { holderId: string; docKind: PolicyDocKind },
  ) => Promise<PolicyUploadResult>;
  /** Live holder typeahead — required when NOT prefilled (the Policies-page entry point). */
  onSearchHolders?: (q: string) => Promise<HolderOption[]>;
  /** Fired once the upload is accepted (parent invalidates the list + toasts). */
  onUploaded?: () => void;
}

type SubmitState = 'idle' | 'saving' | 'failed';

export function UploadPolicyDialog({
  open,
  onOpenChange,
  prefilledHolder,
  onUpload,
  onSearchHolders,
  onUploaded,
}: UploadPolicyDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [docKind, setDocKind] = useState<PolicyDocKind>('police_neu');
  const [state, setState] = useState<SubmitState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Holder selection (only used when NOT prefilled).
  const [holderQuery, setHolderQuery] = useState('');
  const [holderResults, setHolderResults] = useState<HolderOption[]>([]);
  const [selectedHolder, setSelectedHolder] = useState<HolderOption | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setDocKind('police_neu');
      setState('idle');
      setErrorMsg(null);
      setHolderQuery('');
      setHolderResults([]);
      setSelectedHolder(prefilledHolder ?? null);
      setSearching(false);
    }
  }, [open, prefilledHolder]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && state !== 'saving') onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange, state]);

  // Debounced holder typeahead — only when not prefilled and a search fn is provided.
  useEffect(() => {
    if (!open || prefilledHolder || !onSearchHolders) return;
    const q = holderQuery.trim();
    if (q.length < 2) {
      setHolderResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      onSearchHolders(q)
        .then((res) => {
          if (!cancelled) setHolderResults(res);
        })
        .catch(() => {
          if (!cancelled) setHolderResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, prefilledHolder, onSearchHolders, holderQuery]);

  const canSave = file !== null && selectedHolder !== null && state !== 'saving';

  async function handleSubmit() {
    if (!file || !selectedHolder) return;
    setState('saving');
    setErrorMsg(null);
    try {
      await onUpload(file, { holderId: selectedHolder.id, docKind });
      onUploaded?.();
      onOpenChange(false);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Police konnte nicht hochgeladen werden.');
      setState('failed');
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-xl border bg-card shadow-lg">
        <div className="flex items-start justify-between gap-2 border-b p-4">
          <div className="flex flex-col gap-0.5 pr-4">
            <h2 className="flex items-center gap-2 font-semibold text-foreground">
              <Upload className="size-4 text-primary" />
              Neue Police hochladen
            </h2>
            <p className="text-xs text-muted-foreground">
              Policen-PDF hochladen: wird extrahiert, klassifiziert &amp; dem Kunden zugeordnet.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label="Schliessen"
            className="size-7 shrink-0"
          >
            <X className="size-4" />
          </Button>
        </div>

        <form
          id="upload-policy-form"
          className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto p-4"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          {/* Holder (VN) — fixed when prefilled, else a live typeahead. */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-muted-foreground">
              Kunde (Versicherungsnehmer) <span className="text-sem-alert">*</span>
            </label>
            {prefilledHolder ? (
              <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm text-foreground">
                {prefilledHolder.name}
              </div>
            ) : selectedHolder ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm text-foreground">
                <span className="truncate">{selectedHolder.name}</span>
                <button
                  type="button"
                  className="shrink-0 text-xs text-muted-foreground underline underline-offset-2 hover:no-underline"
                  onClick={() => setSelectedHolder(null)}
                >
                  Ändern
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={holderQuery}
                    onChange={(e) => setHolderQuery(e.target.value)}
                    placeholder="Kunde suchen (mind. 2 Zeichen) …"
                    aria-label="Kunde suchen"
                    className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                {holderQuery.trim().length >= 2 && (
                  <div className="max-h-40 overflow-y-auto rounded-md border border-border bg-card">
                    {searching ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">Suche läuft …</div>
                    ) : holderResults.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">Keine Treffer.</div>
                    ) : (
                      <ul className="divide-y divide-border">
                        {holderResults.map((h) => (
                          <li key={h.id}>
                            <button
                              type="button"
                              onClick={() => setSelectedHolder(h)}
                              className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-accent"
                            >
                              <span className="text-sm text-foreground">{h.name}</span>
                              {h.subtitle && (
                                <span className="text-xs text-muted-foreground">{h.subtitle}</span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="upload-policy-file" className="text-xs text-muted-foreground">
              Policen-PDF <span className="text-sem-alert">*</span>
            </label>
            <input
              id="upload-policy-file"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-secondary-foreground"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="upload-policy-kind" className="text-xs text-muted-foreground">
              Art
            </label>
            <select
              id="upload-policy-kind"
              value={docKind}
              onChange={(e) => setDocKind(e.target.value as PolicyDocKind)}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {(Object.entries(DOC_KIND_LABELS) as Array<[PolicyDocKind, string]>).map(
                ([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ),
              )}
            </select>
          </div>

          {state === 'failed' && errorMsg && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-sem-alert/40 bg-sem-alert-bg px-3 py-2 text-sm text-sem-alert"
            >
              <AlertTriangle className="size-4 shrink-0" />
              {errorMsg}
            </div>
          )}
        </form>

        <div className="flex items-center justify-end gap-2 border-t p-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={state === 'saving'}>
            Abbrechen
          </Button>
          <Button type="submit" form="upload-policy-form" disabled={!canSave}>
            <Upload className="size-4" />
            {state === 'saving' ? 'Lädt hoch …' : 'Hochladen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
